import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, Loader, X as XIcon, CheckCircle2 } from 'lucide-react';
import { api } from '@/lib/api';
import { useLang } from '@/context/LangContext';
import { toast } from 'sonner';

/**
 * One-click button: enriches every play owned by the user in the current UI
 * language. Backend runs as a background task; we poll status every 3s and
 * show a progress modal.
 */
export default function BulkEnrichButton({ totalHint = 0 }) {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const [force, setForce] = useState(false);
  const pollRef = useRef(null);

  // Resume polling on mount if a job is already running for this user.
  useEffect(() => {
    let cancelled = false;
    api.get('/translate/plays/enrich-bulk/active/me')
      .then(r => {
        if (cancelled) return;
        const active = r.data?.job;
        if (active) {
          setJob(active);
          setOpen(true);
          startPolling(active.id);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; stopPolling(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const startPolling = (jobId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/translate/plays/enrich-bulk/${jobId}`);
        setJob(r.data);
        if (r.data.status !== 'running') {
          stopPolling();
          if (r.data.status === 'done') {
            toast.success(t('bulk_enrich_done'));
          }
        }
      } catch {
        // ignore transient errors
      }
    }, 3000);
  };

  const start = async () => {
    setStarting(true);
    try {
      // Admin backfill always covers EVERY active language in one shot —
      // that's why we pass lang=all (backend iterates ACTIVE_LANGS).
      const r = await api.post(`/translate/plays/enrich-bulk?lang=all&force=${force}`);
      const jobId = r.data.job_id;
      // Fetch initial state right away
      const init = await api.get(`/translate/plays/enrich-bulk/${jobId}`);
      setJob(init.data);
      setOpen(true);
      startPolling(jobId);
    } catch (e) {
      toast.error(t('bulk_enrich_start_error'));
    } finally {
      setStarting(false);
    }
  };

  const done = job && job.status !== 'running';
  const total = job?.total ?? totalHint;
  const processed = (job?.completed ?? 0) + (job?.skipped ?? 0) + (job?.failed ?? 0);
  const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;

  return (
    <>
      <button
        data-testid="bulk-enrich-btn"
        onClick={() => job?.status === 'running' ? setOpen(true) : start()}
        disabled={starting}
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md border-2 border-gold bg-gradient-to-r from-gold/15 via-gold/5 to-transparent text-gold hover:from-gold/25 hover:to-gold/5 transition font-h uppercase tracking-wider text-sm font-semibold disabled:opacity-50"
        title={t('bulk_enrich_hint')}
      >
        {starting ? <Loader className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
        {job?.status === 'running'
          ? `${t('bulk_enrich_running')} ${processed}/${total || '...'}`
          : t('bulk_enrich_btn')}
      </button>

      <label className="mt-2 flex items-center gap-2 text-xs text-gray-400 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={force}
          onChange={e => setForce(e.target.checked)}
          data-testid="bulk-enrich-force"
          className="accent-gold"
        />
        {t('bulk_enrich_force_label')}
      </label>

      {open && job && (
        <div
          className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-3"
          onClick={() => setOpen(false)}
          data-testid="bulk-enrich-modal"
        >
          <div
            className="bg-card-steel border-2 border-gold/50 rounded-lg w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-h uppercase text-xl flex items-center gap-2 text-gold">
                {done
                  ? <><CheckCircle2 className="w-5 h-5" /> {t('bulk_enrich_done_title')}</>
                  : <><Sparkles className="w-5 h-5 animate-pulse" /> {t('bulk_enrich_in_progress')}</>}
              </h3>
              <button onClick={() => setOpen(false)} aria-label={t('close')} data-testid="bulk-enrich-x" className="text-gray-400 hover:text-white">
                <XIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="text-xs text-gray-400 uppercase tracking-widest font-mono mb-1">
              {t('admin_active_langs')}: {(job.langs || []).join(', ').toUpperCase() || lang.toUpperCase()}
            </div>

            <div className="mt-3">
              <div className="flex items-center justify-between text-xs mb-1.5 font-mono">
                <span className="text-gold">{processed}/{total}</span>
                <span className="text-gray-400">{pct}%</span>
              </div>
              <div className="h-2 bg-navy-deep rounded overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-gold to-gold-light transition-all duration-500"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>

            <div className="mt-4 space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-gray-400">{t('bulk_enrich_completed')}</span><span className="text-gold font-mono">{job.completed}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">{t('bulk_enrich_skipped')}</span><span className="text-gray-300 font-mono">{job.skipped}</span></div>
              {job.failed > 0 && (
                <div className="flex justify-between"><span className="text-gray-400">{t('bulk_enrich_failed')}</span><span className="text-alert font-mono">{job.failed}</span></div>
              )}
            </div>

            {job.status === 'running' && job.current_play_name && (
              <div className="mt-3 text-xs text-gray-300 italic">
                {t('bulk_enrich_current')}: <span className="text-gold not-italic">{job.current_play_name}</span>
              </div>
            )}

            {done && job.errors?.length > 0 && (
              <details className="mt-3 text-xs">
                <summary className="cursor-pointer text-alert">{t('bulk_enrich_errors')} ({job.errors.length})</summary>
                <ul className="mt-1 space-y-0.5 max-h-32 overflow-y-auto text-gray-400 font-mono">
                  {job.errors.map((e, i) => <li key={i}>• {e}</li>)}
                </ul>
              </details>
            )}

            {!done && (
              <p className="mt-4 text-[11px] text-gray-500 leading-relaxed">
                {t('bulk_enrich_background_note')}
              </p>
            )}

            {done && (
              <button
                onClick={() => setOpen(false)}
                className="mt-4 w-full px-4 py-2 rounded-md bg-gold text-navy font-h uppercase tracking-wider font-semibold hover:bg-gold-light"
                data-testid="bulk-enrich-close"
              >
                {t('close')}
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}
